// partners/adminView/pages/A1_AdminOverview.jsx
// Program-wide stats — applications, partners, referrals, financials.

import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useGetAdminStatsQuery } from "../../../apis/adminApiSlice";
import AdminLayout from "./A0_AdminLayout";
import { capturePartnerEvent } from "../../../utils/analytics";

function StatBox({ label, value, sub }) {
  return (
    <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-5">
      <div className="text-xs text-mnf-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div className="font-poppins text-2xl font-bold">{value ?? "—"}</div>
      {sub && <div className="text-xs text-mnf-text-muted mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminOverview() {
  const { data, isLoading } = useGetAdminStatsQuery();
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && data && !viewedRef.current) {
      viewedRef.current = true;
      capturePartnerEvent("partner_admin_overview_viewed");
    }
  }, [isLoading, data]);

  if (isLoading)
    return (
      <AdminLayout>
        <div className="text-mnf-text-muted text-sm">Loading...</div>
      </AdminLayout>
    );

  const apps     = data?.applications || {};
  const partners = data?.partners     || {};
  const refs     = data?.referrals    || {};
  const fin      = data?.financials   || {};

  return (
    <AdminLayout>
      <h1 className="font-poppins text-2xl font-bold mb-8">Partner Program Overview</h1>

      {/* Applications */}
      <section className="mb-10">
        <h2 className="font-poppins text-sm font-semibold text-mnf-text-muted uppercase tracking-widest mb-4">
          Applications
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatBox label="Total"    value={apps.total}    />
          <StatBox label="Pending"  value={apps.pending}  />
          <StatBox label="Approved" value={apps.approved} />
          <StatBox label="Rejected" value={apps.rejected} />
        </div>
      </section>

      {/* Partners */}
      <section className="mb-10">
        <h2 className="font-poppins text-sm font-semibold text-mnf-text-muted uppercase tracking-widest mb-4">
          Active Partners
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <StatBox label="Total Active" value={partners.total_active}                      />
          <StatBox label="Ecosystem"    value={partners.by_type?.ecosystem ?? 0}           />
          <StatBox label="Ambassadors"  value={partners.by_type?.creative_ambassador ?? 0} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-4">
          {["bronze", "silver", "gold", "platinum", "diamond"].map((t) => (
            <StatBox
              key={t}
              label={t.charAt(0).toUpperCase() + t.slice(1)}
              value={partners.by_tier?.[t] ?? 0}
            />
          ))}
        </div>
      </section>

      {/* Referrals */}
      <section className="mb-10">
        <h2 className="font-poppins text-sm font-semibold text-mnf-text-muted uppercase tracking-widest mb-4">
          Referrals
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatBox label="Total Clicks"     value={refs.total_clicks?.toLocaleString("en-IN")}    />
          <StatBox label="Signups"          value={refs.total_signups?.toLocaleString("en-IN")}   />
          <StatBox label="Converted"        value={refs.total_converted?.toLocaleString("en-IN")} />
          <StatBox label="Conversion Rate"  value={`${refs.avg_conversion_rate ?? 0}%`}          />
        </div>
      </section>

      {/* Financials */}
      <section className="mb-10">
        <h2 className="font-poppins text-sm font-semibold text-mnf-text-muted uppercase tracking-widest mb-4">
          Financials
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatBox label="Total Commissions" value={`$${Number(fin.total_commissions || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`} />
          <StatBox label="Total Paid Out"    value={`$${Number(fin.total_paid        || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`} />
          <StatBox label="Pending Payouts"   value={`$${Number(fin.pending_payouts   || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`} />
        </div>
      </section>

      {/* Quick links */}
      <div className="flex gap-4 flex-wrap">
        <Link to="/partners/admin/applications" className="text-sm text-mnf-cyan hover:underline">Review applications →</Link>
        <Link to="/partners/admin/payouts"      className="text-sm text-mnf-cyan hover:underline">Process payouts →</Link>
        <Link to="/partners/admin/milestones"   className="text-sm text-mnf-cyan hover:underline">Pay milestones →</Link>
      </div>
    </AdminLayout>
  );
}
